import i18n from "../../i18n";

/** LocalTime strings accepted by anomaly time-gate APIs: "HH:mm" or "HH:mm:ss". */
export type LocalTimeString = string;

const LOCAL_TIME_PATTERN = /^(\d{2}):(\d{2})(?::(\d{2}))?$/;

export type CustomerWorkHoursSource = {
  startWorkTime?: string | null;
  endWorkTime?: string | null;
  timeZone?: string | null;
};

/** Convert API LocalTime / legacy datetime into HTML `type="time"` value (`HH:mm`). */
export function toLocalTimeInputValue(value?: string | null): string {
  const trimmed = typeof value === "string" ? value.trim() : "";
  if (!trimmed) {
    return "";
  }

  const timeMatch = LOCAL_TIME_PATTERN.exec(trimmed);
  if (timeMatch) {
    return `${timeMatch[1]}:${timeMatch[2]}`;
  }

  const isoMatch = /T(\d{2}):(\d{2})/.exec(trimmed);
  if (isoMatch) {
    return `${isoMatch[1]}:${isoMatch[2]}`;
  }

  return "";
}

/** Normalize form time to API LocalTime (`HH:mm:ss`). */
export function toApiLocalTimeValue(value?: string | null): string | undefined {
  const trimmed = typeof value === "string" ? value.trim() : "";
  if (!trimmed) {
    return undefined;
  }

  const match = LOCAL_TIME_PATTERN.exec(trimmed);
  if (!match) {
    return undefined;
  }

  return `${match[1]}:${match[2]}:${match[3] ?? "00"}`;
}

/** Minutes from midnight for LocalTime comparison (supports overnight ranges). */
export function localTimeToMinutes(value?: string | null): number | null {
  const normalized = toApiLocalTimeValue(value);
  if (!normalized) {
    return null;
  }

  const [hours, minutes, seconds] = normalized.split(":").map((part) => Number(part));
  if (![hours, minutes, seconds].every((part) => Number.isInteger(part))) {
    return null;
  }

  return hours * 60 + minutes + Math.floor(seconds / 60);
}

export function formatLocalTimeLabel(value?: string | null): string {
  const input = toLocalTimeInputValue(value);
  return input || "—";
}

export function hasCustomerWorkHours(customer?: CustomerWorkHoursSource | null): boolean {
  return Boolean(toLocalTimeInputValue(customer?.startWorkTime) && toLocalTimeInputValue(customer?.endWorkTime));
}

export function resolveCustomerTimeZone(customer?: CustomerWorkHoursSource | null): string {
  const zone = customer?.timeZone?.trim();
  return zone || "Europe/Istanbul";
}

export function formatCustomerWorkHoursLabel(customer?: CustomerWorkHoursSource | null): string {
  const start = toLocalTimeInputValue(customer?.startWorkTime);
  const end = toLocalTimeInputValue(customer?.endWorkTime);
  const zone = resolveCustomerTimeZone(customer);

  if (!start || !end) {
    return i18n.t("anomalies.timeGates.workHoursMissing", { timeZone: zone });
  }

  return i18n.t("anomalies.timeGates.workHoursLabel", { start, end, timeZone: zone });
}

export function formatTimeWindowLabel(validFrom?: string | null, validTo?: string | null): string | null {
  const from = toLocalTimeInputValue(validFrom);
  const to = toLocalTimeInputValue(validTo);
  if (!from || !to) {
    return null;
  }

  const fromMinutes = localTimeToMinutes(from);
  const toMinutes = localTimeToMinutes(to);
  const overnight =
    fromMinutes != null && toMinutes != null && fromMinutes > toMinutes
      ? i18n.t("anomalies.overnightSuffix")
      : "";

  return `${from} – ${to}${overnight}`;
}

export function isOvernightTimeWindow(validFrom?: string | null, validTo?: string | null): boolean {
  const fromMinutes = localTimeToMinutes(validFrom);
  const toMinutes = localTimeToMinutes(validTo);
  return fromMinutes != null && toMinutes != null && fromMinutes > toMinutes;
}

export function parseOutsideWorkingHoursFlag(value: unknown): boolean {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    return value.trim().toLowerCase() === "true" || value.trim() === "1";
  }
  return false;
}

export function validateAnomalyTimeGates(
  formJson: Record<string, string>,
  options?: { customer?: CustomerWorkHoursSource | null; allowMissingWorkHours?: boolean },
): string | null {
  const validFromRaw = formJson.validFrom?.trim() ?? "";
  const validToRaw = formJson.validTo?.trim() ?? "";
  const validFrom = toApiLocalTimeValue(validFromRaw);
  const validTo = toApiLocalTimeValue(validToRaw);

  if ((validFromRaw && !validToRaw) || (!validFromRaw && validToRaw)) {
    return i18n.t("anomalies.timeGates.bothTimesRequired");
  }

  if (validFromRaw && !validFrom) {
    return i18n.t("anomalies.timeGates.invalidStart");
  }

  if (validToRaw && !validTo) {
    return i18n.t("anomalies.timeGates.invalidEnd");
  }

  const outsideWorkingHours = parseOutsideWorkingHoursFlag(formJson.outsideWorkingHours);
  if (
    outsideWorkingHours &&
    !options?.allowMissingWorkHours &&
    !hasCustomerWorkHours(options?.customer)
  ) {
    return i18n.t("anomalies.timeGates.workHoursRequired");
  }

  return null;
}

export function buildTimeGateSummary(options: {
  validFrom?: string | null;
  validTo?: string | null;
  outsideWorkingHours?: boolean;
  customer?: CustomerWorkHoursSource | null;
}): string | null {
  const windowLabel = formatTimeWindowLabel(options.validFrom, options.validTo);
  const workHours = hasCustomerWorkHours(options.customer)
    ? `${toLocalTimeInputValue(options.customer?.startWorkTime)}–${toLocalTimeInputValue(options.customer?.endWorkTime)}`
    : null;

  if (!windowLabel && !options.outsideWorkingHours) {
    return null;
  }

  if (windowLabel && options.outsideWorkingHours) {
    return workHours
      ? i18n.t("anomalies.timeGates.summaryBoth", { window: windowLabel, workHours })
      : i18n.t("anomalies.timeGates.summaryBothNoHours", { window: windowLabel });
  }

  if (windowLabel) {
    return i18n.t("anomalies.timeGates.summaryWindow", { window: windowLabel });
  }

  return workHours
    ? i18n.t("anomalies.timeGates.summaryOutside", { workHours })
    : i18n.t("anomalies.timeGates.summaryOutsideNoHours");
}
